/* eslint-disable no-console */
import './template-modal.scss';

import { useMutation } from '@apollo/client';
import { usePrevious } from '@uidotdev/usehooks';
import { filter, kebabCase, omit, orderBy } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../../data/context/app-context';
import { useHonorificTemplateBlockTimeValues } from '../../../../data/data/honorific-block-times/select-values';
import { useTemplateItem } from '../../../../data/data/templates/use-template-item';
import { TemplateEntryItem } from '../../../../data/data/types';
import { isTemplateExceptionValidForAppointmentBlock } from '../../../../data/data/utils';
import {
  AgendaAppointmentItemDataFragment,
  GetTemplateItemsDocument,
  Honorific,
  TemplateEntryInput,
  TemplateEntryType,
  TemplateItemDataFragment,
  TemplateStatus,
  UpdateTemplateItemDocument,
  ValidateTemplateEntryItemsDocument,
} from '../../../../data/types/api/graphql';
import {
  formatDate,
  formatTimeSpanToTime,
} from '../../../../data/utils/date.utils';
import { FormError } from '../../../../data/utils/form.utils';
import { useNavigateWithHealthProvider } from '../../../../data/utils/router.utils';
import { AlertMessageType } from '../../../../elements/alert-message/alert-message';
import { AppointmentExceptionList } from '../../../../elements/appointment-exception-list/appointment-exception-list';
import { AppointmentExceptionListItemType } from '../../../../elements/appointment-exception-list/appointment-exception-list-item';
import {
  Button,
  ButtonIcon,
  ButtonType,
} from '../../../../elements/button/button';
import { InputCheckboxController } from '../../../../elements/form/controllers/input-checkbox';
import { InputFieldController } from '../../../../elements/form/controllers/input-field';
import { FormRow } from '../../../../elements/form/form';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { Message, MessageType } from '../../../../elements/message/message';
import { Modal, ModalWidth } from '../../../../elements/modal/modal';
import { ModalPrompt } from '../../../../elements/modal/modal-prompt';
import { Tag, TagType } from '../../../../elements/tag/tag';
import { TemplateEntryList } from '../../../../elements/template-entry-list/template-entry-list';
import { TemplateEntryForm } from '../../../../elements/template-enty-form/template-entry-form';
import { useTemplateForm } from '../../form/use-template-form';
import { TemplateCopyPrompt } from '../template-copy-prompt/template-copy-prompt';
import { TemplateDeletePrompt } from '../template-delete-prompt/template-delete-prompt';

export enum TemplateModalAction {
  DEFAULT = 'DEFAULT',
  UPDATE = 'UPDATE',
}

type TemplateModalProps = {
  action?: TemplateModalAction;
  templateId: string | null | undefined;
  onSaved?: (value: TemplateItemDataFragment) => void;
  onCancel?: () => void;
  onClose?: () => void;
};

export function TemplateModal({
  templateId,
  onSaved,
  onCancel,
  onClose,
  ...props
}: TemplateModalProps) {
  const intl = useIntl();

  const { showAlertMessage } = useAppContext();

  const [action, setAction] = useState<TemplateModalAction>(
    TemplateModalAction.DEFAULT
  );

  useEffect(() => {
    setAction(props.action ?? TemplateModalAction.DEFAULT);
  }, [props.action]);

  // get template data
  const { data: templateItemData, loading: templateItemLoading } =
    useTemplateItem(templateId);

  // get honorific values
  const {
    data: honorificBlockTimeItemsData,
    loading: honorificBlockTimeItemsLoading,
  } = useHonorificTemplateBlockTimeValues(
    templateItemData?.healthProviderId,
    templateItemLoading
  );

  // get available honorific values
  const availableHonoficBlockTimeItems: {
    key: Honorific;
    title: string;
    value: number;
  }[] = useMemo(
    () => honorificBlockTimeItemsData?.filter(x => x.value > 0) ?? [],
    [honorificBlockTimeItemsData]
  );

  const { form, formIsValid, setErrors } = useTemplateForm();

  const [
    validateTemplateEntryItems,
    { loading: validateTemplateEntryItemsLoading },
  ] = useMutation(ValidateTemplateEntryItemsDocument, {
    fetchPolicy: 'no-cache',
  });

  const entries = form.watch('entries');
  const overwrite = form.watch('overwrite') === true;

  const [existingAppointments, setExistingAppointments] = useState<
    AgendaAppointmentItemDataFragment[]
  >([]);

  const validate = useCallback(
    async (
      currentEntries: TemplateEntryInput[],
      currentTemplateId?: string | null | undefined
    ) => {
      const result = await validateTemplateEntryItems({
        variables: {
          healthProviderId: templateItemData?.healthProviderId,
          honorific: templateItemData?.honorific as Honorific,
          templateId: currentTemplateId ?? null,
          overWriteConsultTypeElse: overwrite,
          entries: currentEntries.map(entry =>
            omit(
              {
                ...entry,
                id: entry.id.includes('temp-id') ? null : entry.id,
                minuteLength: entry.minuteLength ?? 0,
                appointments: entry.appointments.map(appointment =>
                  omit(
                    {
                      ...appointment,
                      id: appointment.id.includes('temp-id')
                        ? null
                        : appointment.id,
                      minuteLength: appointment.minuteLength ?? 0,
                    },
                    '__typename',
                    'appointments'
                  )
                ),
              },
              '__typename'
            )
          ),
        },
      });
      setExistingAppointments(
        result.data?.templateValidateEntries.templateApply
          ?.existingAppointments ?? []
      );

      console.log(result.data);
      if (result.data?.templateValidateEntries.errors) {
        console.log(result.data?.templateValidateEntries.errors);
        setErrors(result.data?.templateValidateEntries.errors);
      }
    },
    [
      overwrite,
      setErrors,
      templateItemData?.healthProviderId,
      templateItemData?.honorific,
      validateTemplateEntryItems,
    ]
  );

  // on overwrite toggle and initial (prevOverwrite is false default)
  const prevOverwrite = usePrevious(overwrite);
  useEffect(() => {
    if (action === TemplateModalAction.UPDATE && templateId) {
      if (prevOverwrite !== overwrite) {
        validate(entries, templateId);
      }
    }
  }, [action, entries, overwrite, prevOverwrite, templateId, validate]);

  // set form
  // const [initialized, setInitialized] = useState(false);
  const initialized = useRef<boolean>(false);
  useEffect(() => {
    if (
      templateItemData &&
      honorificBlockTimeItemsData &&
      !initialized.current
    ) {
      initialized.current = true;

      const currentEntries = templateItemData.entries.map(entry =>
        omit(
          {
            ...entry,
            minuteLength: entry.minuteLength ?? 0,
            appointments: entry.appointments.map(appointment =>
              omit(
                {
                  ...appointment,
                  minuteLength: appointment.minuteLength ?? 0,
                },
                '__typename',
                'appointments'
              )
            ),
          },
          '__typename'
        )
      );

      form.reset({
        ...templateItemData,
        honorific: templateItemData?.honorific as Honorific,
        overwrite: false,
        entries: currentEntries,
      });
    }
  }, [
    templateItemData,
    form,
    validate,
    templateId,
    honorificBlockTimeItemsData,
  ]);

  const blockSize = useMemo(() => {
    if (templateItemData?.honorific) {
      const honorificBlockTimeItem = availableHonoficBlockTimeItems.find(
        item => item.key === templateItemData?.honorific
      );
      return honorificBlockTimeItem?.value ?? null;
    }
    return availableHonoficBlockTimeItems[0]?.value ?? null;
  }, [templateItemData?.honorific, availableHonoficBlockTimeItems]);

  const onAddEntry = useCallback(
    (value: TemplateEntryItem) => {
      let newEntries = [...entries];

      if (value.type === TemplateEntryType.Exception) {
        newEntries = [...entries].map(option => {
          const appointments = [...option.appointments];

          if (isTemplateExceptionValidForAppointmentBlock(value, option)) {
            appointments.push(value);
          }

          return {
            ...option,
            appointments: orderBy(appointments, entry => [
              formatTimeSpanToTime(entry.minuteStartTime),
            ]),
          };
        });
      } else {
        newEntries.push({ ...value, appointments: [] });
      }

      form.setValue(
        'entries',
        orderBy(newEntries, entry =>
          formatTimeSpanToTime(entry.minuteStartTime)
        ),
        {
          shouldDirty: true,
        }
      );
    },
    [entries, form]
  );

  const onRemoveEntry = useCallback(
    async (value: string) => {
      const newEntries = [...entries]
        .map(entry => ({
          ...entry,
          appointments: entry.appointments.filter(
            appointment => appointment.id !== value
          ),
        }))
        .filter(entry => entry.id !== value);

      form.setValue(
        'entries',
        orderBy(newEntries, entry =>
          formatTimeSpanToTime(entry.minuteStartTime)
        ),
        {
          shouldDirty: true,
        }
      );
      validate(newEntries, templateId);
    },
    [entries, form, templateId, validate]
  );

  const [updateTemplateItem, { loading: updateTemplateItemLoading }] =
    useMutation(UpdateTemplateItemDocument, {
      refetchQueries: [GetTemplateItemsDocument],
    });

  const onHandleSubmit = useCallback(() => {
    form.handleSubmit(async () => {
      const values = form.getValues();

      try {
        const result = await updateTemplateItem({
          variables: {
            ...values,
            healthProviderId: templateItemData?.healthProviderId,
            honorific: templateItemData?.honorific as Honorific,
            id: templateId ?? '',
            overWriteConsultTypeElse: overwrite,
            entries: values.entries.map(entry =>
              omit(
                {
                  ...entry,
                  id: entry.id.includes('temp-id') ? null : entry.id,
                  appointments: entry.appointments.map(appointment =>
                    omit(
                      {
                        ...appointment,
                        id: appointment.id.includes('temp-id')
                          ? null
                          : appointment.id,
                      },
                      '__typename',
                      'appointments'
                    )
                  ),
                },
                '__typename'
              )
            ),
          },
        });

        if (result.data?.templateUpdate.template) {
          showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: intl.formatMessage({
              id: 'template.template-update.success',
            }),
          });

          onSaved?.(result.data.templateUpdate.template);

          onClose?.();
        } else {
          setErrors(result.data?.templateUpdate?.errors);
          (result.data?.templateUpdate?.errors as FormError[]).forEach(e => {
            showAlertMessage({
              type: AlertMessageType.ERROR,
              message: intl.formatMessage({
                id: `common.errors.${kebabCase(e.message)}`,
                defaultMessage: e.message,
              }),
            });
          });
        }
      } catch (error) {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: (error as Error).message,
        });
      }
    })();
  }, [
    form,
    updateTemplateItem,
    templateItemData?.healthProviderId,
    templateItemData?.honorific,
    templateId,
    overwrite,
    showAlertMessage,
    intl,
    onSaved,
    onClose,
    setErrors,
  ]);

  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [copyModalOpen, setCopyModalOpen] = useState<boolean>(false);

  const [cancelPromptOpen, setCancelPromptOpen] = useState<boolean>(false);

  const onHandleCancel = useCallback(
    (confirmed: boolean = false) => {
      if (action === TemplateModalAction.UPDATE) {
        if (!confirmed && form.formState.isDirty) {
          setCancelPromptOpen(true);
        } else {
          setExistingAppointments([]);
          form.reset();
          setAction(TemplateModalAction.DEFAULT);
        }
      } else {
        onCancel?.();

        onClose?.();
      }
    },
    [action, form, onCancel, onClose]
  );

  const navigate = useNavigateWithHealthProvider();
  const onHandleAppointmentException = useCallback(
    (appointment: AgendaAppointmentItemDataFragment) => {
      onCancel?.();

      onClose?.();

      navigate(
        `/agenda/${appointment.healthProfessional?.id}/${formatDate(
          appointment.startTime
        )}`
      );
    },
    [navigate, onCancel, onClose]
  );

  const isLoading = useMemo(
    () =>
      updateTemplateItemLoading ||
      validateTemplateEntryItemsLoading ||
      honorificBlockTimeItemsLoading,
    [
      honorificBlockTimeItemsLoading,
      updateTemplateItemLoading,
      validateTemplateEntryItemsLoading,
    ]
  );

  const isDisabled = useMemo(
    () =>
      !formIsValid ||
      updateTemplateItemLoading ||
      validateTemplateEntryItemsLoading ||
      existingAppointments.length > 0 ||
      entries.length === 0,
    [
      entries.length,
      existingAppointments.length,
      formIsValid,
      updateTemplateItemLoading,
      validateTemplateEntryItemsLoading,
    ]
  );

  return (
    <>
      <Modal
        testId="template"
        loading={templateItemLoading}
        title={
          action !== TemplateModalAction.DEFAULT ? (
            <CustomFormattedMessage
              id={`template.template-${kebabCase(action)}.title`}
            />
          ) : (
            <CustomFormattedMessage id="template.template.title" />
          )
        }
        width={ModalWidth.LARGE}
        onClose={onHandleCancel}
        buttons={
          action === TemplateModalAction.UPDATE ? (
            <>
              <Button
                type={ButtonType.SECONDARY}
                inverted
                onClick={() => onHandleCancel(false)}
              >
                <CustomFormattedMessage id="common.cancel" />
              </Button>

              <Button
                icon={isLoading ? ButtonIcon.LOADING : ButtonIcon.NONE}
                disabled={isDisabled}
                onClick={onHandleSubmit}
              >
                <CustomFormattedMessage id="template.template-update.save" />
              </Button>
            </>
          ) : (
            <>
              <Button inverted onClick={() => setCopyModalOpen(true)}>
                <CustomFormattedMessage id="template.button.copy" />
              </Button>

              <Button inverted onClick={() => setDeleteModalOpen(true)}>
                <CustomFormattedMessage id="template.button.delete" />
              </Button>

              <Button
                onClick={() => {
                  setExistingAppointments([]);
                  setAction(TemplateModalAction.UPDATE);
                }}
              >
                <CustomFormattedMessage id="template.button.update" />
              </Button>
            </>
          )
        }
      >
        {action === TemplateModalAction.UPDATE ? (
          <FormProvider {...form}>
            <FormRow>
              <InputFieldController
                name="name"
                label={
                  <>
                    <CustomFormattedMessage id="common.name" />
                    <small>*</small>
                  </>
                }
                placeholder={intl.formatMessage({ id: 'common.name' })}
              />
            </FormRow>

            <div className="template-modal-info__honorific">
              <h4>
                <CustomFormattedMessage id="common.honorific" />
              </h4>
              <p>
                <CustomFormattedMessage
                  id={`common.honorific.${kebabCase(
                    templateItemData?.honorific
                  )}`}
                />{' '}
                <span>
                  (
                  <CustomFormattedMessage
                    id="common.minimum-block-size"
                    values={{ minutes: blockSize }}
                  />
                  )
                </span>
              </p>
            </div>

            <h4>
              <CustomFormattedMessage id="template.template-detail" />
            </h4>
            {templateItemData && availableHonoficBlockTimeItems.length > 0 && (
              <TemplateEntryForm
                healthProviderId={templateItemData?.healthProviderId}
                honorific={templateItemData?.honorific as Honorific}
                blockSize={blockSize ?? 0}
                templateId={templateId}
                overwrite={overwrite}
                entries={entries}
                onSave={onAddEntry}
              />
            )}

            <h4>
              <CustomFormattedMessage id="template.template-details" />
            </h4>
            <TemplateEntryList
              items={entries}
              actions={[
                {
                  icon: ButtonIcon.MINUS,
                  onClick: onRemoveEntry,
                },
              ]}
            />

            {filter(form.formState.errors, (_option, key) =>
              ['Entries', 'Appointment', 'Exception'].includes(key)
            ).map(option => (
              <Message
                key={option?.message}
                type={MessageType.ERROR}
                message={option?.message}
              />
            ))}

            <FormRow>
              <InputCheckboxController<boolean>
                name="overwrite"
                label={
                  <CustomFormattedMessage id="agenda.template-apply.overwrite" />
                }
                options={[
                  {
                    key: true,
                    title: (
                      <CustomFormattedMessage id="agenda.template-apply.overwrite-placeholder" />
                    ),
                  },
                ]}
                disabled={
                  validateTemplateEntryItemsLoading ||
                  templateItemLoading ||
                  updateTemplateItemLoading
                }
              />
            </FormRow>
            <FormRow>
              <h4>
                <CustomFormattedMessage
                  id="common.agenda-appointment.appointments-blocking"
                  values={{
                    count: existingAppointments.length,
                  }}
                />
              </h4>
              <AppointmentExceptionList
                type={AppointmentExceptionListItemType.WARNING}
                items={existingAppointments}
                includePatient
                includeHealthProfessional
                onClick={onHandleAppointmentException}
              />
            </FormRow>
          </FormProvider>
        ) : (
          <div className="template-modal-info">
            <div className="template-modal-info__content">
              <h3>{templateItemData?.name}</h3>
              <div className="template-modal-info__honorific">
                <h4>
                  <CustomFormattedMessage id="common.honorific" />
                </h4>
                <p>
                  <CustomFormattedMessage
                    id={`common.honorific.${kebabCase(
                      templateItemData?.honorific
                    )}`}
                  />{' '}
                  <span>
                    (
                    <CustomFormattedMessage
                      id="common.minimum-block-size"
                      values={{ minutes: blockSize }}
                    />
                    )
                  </span>
                </p>
              </div>
              <h4>
                <CustomFormattedMessage id="template.template-details" />
              </h4>
              <TemplateEntryList items={templateItemData?.entries ?? []} />
            </div>
            <div className="template-modal-info__tags">
              {templateItemData?.isApplied && (
                <Tag
                  type={TagType.ORANGE}
                  text={<CustomFormattedMessage id="common.used" />}
                />
              )}

              {templateItemData?.status === TemplateStatus.Active && (
                <Tag type={TagType.ACTIVE} />
              )}

              {(templateItemData?.status === TemplateStatus.Deleted ||
                templateItemData?.status === TemplateStatus.Unresolved) && (
                <Tag type={TagType.NOTACTIVE} />
              )}
            </div>
          </div>
        )}
      </Modal>

      {cancelPromptOpen && (
        <ModalPrompt
          onConfirm={() => onHandleCancel(true)}
          onClose={() => setCancelPromptOpen(false)}
        >
          <p>
            <CustomFormattedMessage id="common.confirm-not-saved-changes" />
          </p>
        </ModalPrompt>
      )}

      {deleteModalOpen && templateItemData && (
        <TemplateDeletePrompt
          templateItem={templateItemData}
          onDeleted={() => {
            onClose?.();
          }}
          onClose={() => {
            setDeleteModalOpen(false);
          }}
        />
      )}

      {copyModalOpen && templateItemData && (
        <TemplateCopyPrompt
          templateItem={templateItemData}
          onCopied={() => {
            onClose?.();
          }}
          onClose={() => {
            setCopyModalOpen(false);
          }}
        />
      )}
    </>
  );
}
